import React from 'react';

import t from '../../../labels/labelling-utils';
import { isNumeric } from '../../../utils/numbers';
import { slugify } from '../../../utils/slugify';
import { uppercaseFirstLetter } from '../../../utils/string-utils';
import { IClassNames } from '../../../utils/styles';
import { dateFormat } from '../../../utils/times';
import Cta from '../../atoms/cta/cta';
import Dot from '../../atoms/dot/dot';
import IconChevronRight from '../../atoms/icons/icon-chevron-right';
import ResultsTitle from '../../atoms/results-title/results-title';
import Text from '../../atoms/text/text';
import { ITeam } from '../../molecules/team/team';
import Glider from '../../organisms/glider/glider';

import { ITeamPredictedLineup, PredictedLineupVariant } from './predicted-lineups-hp';

interface ICreateIds {
  home: string;
  away: string;
  full: string;
}

export const getTitle = (home: ITeam, away: ITeam, cn: IClassNames): JSX.Element => (
  <Text tag="h2" className={cn.titleLmp}>
    {t('match.lineups.lmp.title')} {home.name}–{away.name}
  </Text>
);

export const buildGliderLabel = (
  homeShortname: string | undefined,
  awayShortname: string | undefined
): React.ReactNode => (
  <React.Fragment>
    {homeShortname?.toUpperCase() || ''}
    <br />
    {awayShortname?.toUpperCase() || ''}
  </React.Fragment>
);

export const createIds = (home: ITeam, away: ITeam, fallbackKey: number): ICreateIds => {
  const idHome = slugify(/* istanbul ignore next: optional chaining */ home.seoName?.toLowerCase() || `${fallbackKey}`);
  const idAway = slugify(/* istanbul ignore next: optional chaining */ away.seoName?.toLowerCase() || `${fallbackKey}`);
  return {
    home: `${idHome}-home`,
    away: `${idAway}-away`,
    full: `${idHome}-${idAway}`,
  };
};

export const getGliderItems = (matchList: ITeamPredictedLineup[], variant: PredictedLineupVariant) =>
  variant === 'LMP'
    ? []
    : matchList.map((match: ITeamPredictedLineup, key: number) => ({
        href: `#${createIds(match.home, match.away, key).full}`,
        label: buildGliderLabel(
          /* istanbul ignore next: optional chaining */ match?.home?.shortname,
          /* istanbul ignore next: optional chaining */ match?.away?.shortname
        ),
      }));

export const getDate = (date: string, cn: IClassNames) => {
  const dateShort = uppercaseFirstLetter(dateFormat({ date, format: 'ddd D MMM YYYY' }));
  const dateLong = uppercaseFirstLetter(dateFormat({ date, format: 'dddd D MMMM YYYY' }));
  const time = dateFormat({
    date,
    format: 'HH:mm',
  });

  return (
    <React.Fragment>
      <Text fontSize="16" className={cn.dateShort}>
        {' '}
        {dateShort}{' '}
      </Text>
      <Text fontSize="16" className={cn.dateLong}>
        {dateLong}{' '}
      </Text>
      <Text fontSize="16">ore {time}</Text>
    </React.Fragment>
  );
};

export const getCta = (
  cta: string | undefined,
  variant: PredictedLineupVariant,
  trackingData?: object
): JSX.Element | null =>
  cta && variant === 'PREDICTED-LINEUP' ? (
    <Cta
      iconSize="xxxsmall"
      iconPosition="right"
      Icon={IconChevronRight}
      href={cta}
      fontSize="14"
      trackingData={trackingData}
    >
      {t('match.lineups.cta')}
    </Cta>
  ) : null;

export const getLmpCta = (
  match: ITeamPredictedLineup,
  variant: PredictedLineupVariant,
  cn: IClassNames,
  trackingData?: object
): JSX.Element => {
  const { home, away, ctaLmpUrl, date } = match;
  return variant === 'LMP' ? (
    getTitle(home, away, cn)
  ) : (
    <React.Fragment>
      <div className={cn.matchDate}>
        <Text fontSize="16">{getDate(date, cn)}</Text>
      </div>
      {ctaLmpUrl && (
        <React.Fragment>
          <Dot isBlinking />
          <Text className={cn.ctaLmp} fontSize="14" fontWeight="400" color="dark">
            {`${t('match.lineups.formations.title')} ${home.name}-${away.name}.`}
          </Text>
          &nbsp;
          <Cta className={cn.ctaLmp} href={ctaLmpUrl} fontSize="14" trackingData={trackingData}>
            {t('match.lineups.formations.cta')}
          </Cta>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export const PredictedLineupsGlider = ({
  variant,
  cn,
  activeIndex,
  items,
  onSelectedGliderItem,
}: {
  variant: PredictedLineupVariant;
  cn: IClassNames;
  activeIndex: number;
  items: {
    href: string;
    label: React.ReactNode;
  }[];
  onSelectedGliderItem: (id: number) => void;
}): JSX.Element | null => {
  return variant === 'LMP' ? null : (
    <Glider activeIndex={activeIndex} items={items} onSelectedGliderItem={onSelectedGliderItem} />
  );
};

export const getRound = (variant: PredictedLineupVariant, round: string, cn: IClassNames): JSX.Element | null =>
  variant === 'LMP' ? null : (
    <div className={cn.header}>
      <ResultsTitle
        title={isNumeric(round) ? `${t('match.reports.home.match.day')} ${round}` : round}
        selfUpdate
        fontSize="16"
        fontWeight="500"
        color="blue"
      />
    </div>
  );
